import { CreativeServerGenConfig } from "../Creative/CreativeServer";
import { CheckDynamicFormElement, DynamicFormElement } from "../DynamicFormElement";
import { CheckUserAction, ShareActionConfig, UserAction } from "../UserData";
import { FanboxColorName } from "../Utility/Color";
import { NormalToUniformTransform, NumberGenerator, SequentialNumberGenerator, UniformToNormalDistributionContext, UniformToNormalTransform } from "../Utility/Statistical";
import { CreateVoting } from "./ConfigRender";
import { ContextTrigger } from "./ContentContext";
import { convertPartyIdToHex, PartyComponentId } from "./PartyId";

export type IDTranslate = { userDef: string, machineDef: string };

export type PartyConfigData = {
  itemId: string;
  title: string;
  image: string;
  description: string;
  main: ActivityBlockConfigData[];
  optional: { [key: string]: ActivityBlockConfigData }
  media: Media[];
  next?: string;
  chats: { [key: string]: ChatConfigData },
  triggers: ContextTrigger[];
  idTranslates: IDTranslate[];
  actionContext: PartyActionContext;

};

export type Media = {
  mediaType: string;
  url: string;
  length: number;
  startTime: number;
};

export type PartyActionContext = {
  currentArtist: string;
  upcomingArtist: string;
  currentTracks: string[];
  upcomingTracks: string[];
}

export const ExpressionColors = ["#4040FF", "#FF0000", "#D4D435", "#60BF60", "#D435D4", "#35D4D4"];


export type DisplayPanel = "left" | "right";
export type ElementRotate = "pyramid" | "sequential" | "random" | "carousel" | "none";
export type PostLayout = "single-element" | "double-element";
export type PostPanelLayout = "primary-left" | "primary-right" | "primary-only";
export type MessageDisplay = "post" | "element" | "none";
export type ActivityBlockType = 'post' | 'quiz' | 'creative' | "chat" | 'submission';
//export type ChatSource = 'integrated' | 'global' | 'none';
export type ChatType = 'read-only' | 'like-only' | 'comment' | 'reply' | 'threaded';
export type ActivityPosition = 'top' | 'middle' | 'bottom' | 'all' | 'none';
//export type PostChatShareMode = 'shared' | 'alternate';

export type ChatConfigData = {
  itemId: string;
  chatType: ChatType;
  messages: MessageConfigData[];
  threads: ChatConfigData[];
  startTime: number;
  endTime: number;
  voting?: VotesConfigData;
}



export type ActivityBlockConfigData = {
  startTime: number;
  endTime: number;
  activityType: ActivityBlockType;
  //chatSource : ChatSource;
  chatSourceId?: string;
  mediaPosition: ActivityPosition;
  //chatPosition? : ActivityPosition;
  // postChatShareMode? : PostChatShareMode;
  config: PostConfigData | QuizConfigData | CreativeConfigData | CreativeServerGenConfig | ChatConfigData | SubmissionCompetitionConfigData;
  forceOptionalEnd: boolean;
}

export const DefaultActivityBlockConfigData = (): ActivityBlockConfigData => {
  return {
    startTime: 0,
    endTime: 0,
    activityType: 'post',
    //chatSource : 'none',
    mediaPosition: 'none',
    config: DefaultPostConfigData(),
    forceOptionalEnd: false
  }
}

export type PostConfigData = {
  itemId: string;
  title: string;
  author: string;
  userColor?: string;
  elements: PostElementConfigData[];
  layout: PostLayout;
  rotate: ElementRotate;
  chatId?: string;
  messageDisplay: MessageDisplay;
  messageMixRatio?: number;
  voting?: VotesConfigData;
  elementDisplayTime?: number;
  elementIndex?: number;
};

export const DefaultPostConfigData = (): PostConfigData => {
  return {
    itemId: '',
    title: '',
    author: '',
    elements: [],
    layout: 'single-element',
    rotate: 'none',
    messageDisplay: 'none',
  }
}

export type PostElementConfigData = {
  itemId: string;
  title?: string;
  primary: PostPanelConfigData;
  secondary?: PostPanelConfigData;
  layout: PostPanelLayout;
  chatId?: string;
  voting?: VotesConfigData;
};

export type PostPanelConfigData = {
  text?: string;
  imageSrc?: string;
  imageClick?: string;
  buttonText?: string;
  buttonClick?: string;
  buttonColor?: FanboxColorName;
  buttonTextColor?: FanboxColorName;
}

export type MessageConfigData = {
  itemId: string;
  userId: string;
  nickname: string;
  userIcon: string;
  userColor: string;
  title?: string;
  postText?: string;
  bodyText: string;
  voting: VotesConfigData;
  replies: MessageConfigData[];
  image?: string;
  displayStart?: number;
  displayEnd?: number;
};

export const EmptyMessageConfigData = (): MessageConfigData => {
  return {
    itemId: '',
    userIcon: '',
    userColor: '',
    userId: '',
    nickname: '',
    bodyText: '',
    voting: EmpyVotesConfig(),
    replies: [],
  }
}

export const MessageConfigFromPostConfigForFeed = (post: PostConfigData, multiplier: number, online: number, userId: string, elementIndex: number = 0, timeBasis: number = 60000): MessageConfigData => {
  const postElement = post.elements[elementIndex];
  const primaryText = postElement.primary.text;
  const secondaryPanel = postElement.secondary;
  const secondaryText = (secondaryPanel !== undefined ? secondaryPanel.text : undefined) ?? '';
  const postText = primaryText ?? secondaryText;
  const image = postElement.primary.imageSrc !== undefined ? postElement.primary.imageSrc : postElement.secondary?.imageSrc;

  const time = Date.now();

  const popularity = GeneratePopularity(false, multiplier, 0, online);

  const voting: VotesConfigData = {
    itemId: post.itemId + "_voting",
    impressions: 0,
    likes: 0,
    comments: 0,
    popularity: popularity,
    expression: 'inspiring',
    created: time,
    updated: time,
  }

  return {
    itemId: post.itemId,
    title: post.title,
    nickname: post.author,
    userId: userId,
    userIcon: '',
    userColor: post.userColor ?? (() => { const index = Math.floor(Math.random() * ExpressionColors.length); return ExpressionColors[index]; })(),
    bodyText: '',
    postText: postText,
    image: image,
    replies: [],
    voting: voting, //CreateVoting(messageId, expressionType, Date.now(), multiplier, 5000, 15000, boosts, online )
  }

}


export const MessageConfigFromPostConfig = (post: PostConfigData, optionality: number, elementIndex: number = 0, userId?: string, pBodyText?: string, pExpressionType?: ExpressionType): [MessageConfigData, string] => {
  const postElement = post.elements[elementIndex];
  const primaryText = postElement.primary.text;
  const secondaryPanel = postElement.secondary;
  const secondaryText = (secondaryPanel !== undefined ? secondaryPanel.text : undefined) ?? '';
  const postText = primaryText ?? secondaryText;
  const image = postElement.primary.imageSrc !== undefined ? postElement.primary.imageSrc : postElement.secondary?.imageSrc;

  const expressionType: ExpressionType = pExpressionType ?? 'inspiring';

  const partyId = (() => {
    const n = parseInt(post.itemId.slice(0, 2));
    return isNaN(n) ? 0 : n;
  })();

  const chatId: PartyComponentId = {
    id: partyId,
    chat: { id: pBodyText !== undefined ? post.chatId ?? post.itemId : post.itemId }
  }

  const chatHexId = convertPartyIdToHex(chatId);
  const time = Date.now();
  const messageId: PartyComponentId = {
    id: partyId,
    chat: { id: chatId.chat?.id ?? post.itemId, message: { id: time } }
  }

  const messageHexId = convertPartyIdToHex(messageId);


  const popularity = Math.floor(Math.random() * optionality);

  const voting: VotesConfigData = {
    itemId: "usr_" + time + "_" + chatHexId.id + "_voting",
    impressions: 0,
    likes: 0,
    comments: 0,
    popularity: popularity,
    expression: expressionType,
    created: time,
    updated: time,
  }

  return [{
    itemId: messageHexId.id,
    title: post.title,
    nickname: post.author,
    userId: userId ?? 'unknown',
    userIcon: '',
    userColor: post.userColor ?? (() => { const index = Math.floor(Math.random() * ExpressionColors.length); return ExpressionColors[index]; })(),
    bodyText: pBodyText ?? '',
    postText: postText,
    image: image,
    replies: [],
    voting: voting, //CreateVoting(messageId, expressionType, Date.now(), multiplier, 5000, 15000, boosts, online )
  }, chatHexId.id]

}

export type ExpressionType =
  | "undefined"
  | "inspiring"
  | "appreciative"
  | "smart"
  | "perceptive"
  | "informative"
  | "funny";

export const ExpressionArray: ExpressionType[] = ['appreciative', 'funny', 'informative', 'inspiring', 'perceptive', 'smart'];

export const ExpressionTypesDescriptionsAIInput: Map<ExpressionType, string> = (() => {
  const map = new Map<ExpressionType, string>();
  map.set("inspiring", "You found the comment to be very inspiring or motivational");
  map.set("appreciative", "You found the comment to be very appreciative of or grateful for something and you agree with appreciating that thing too, or share the same admiration");
  map.set("smart", "You found the comment to be very smart, insightful, or clever. This could be a deep insight into the topic, a clever observation, or a witty remark");
  map.set("perceptive", "You found the comment to be very perceptive, showing a good understanding of the topic or situation. It is not just smart, but shows a deep understanding of the nuances involved");
  map.set("informative", "You found the comment to be very informative, providing useful information or explanations that you didn't know before");
  map.set("funny", "You found the comment to be very funny, humorous, or entertaining. It made you laugh or smile");
  return map;
})();



export const ConvertExpressionTypeToNumber = (expression: ExpressionType) => {
  if (expression === 'undefined')
    return -1;
  else if (expression === "inspiring")
    return 0;
  else if (expression === "appreciative")
    return 1;
  else if (expression === "smart")
    return 2;
  else if (expression === "perceptive")
    return 3;
  else if (expression === "informative")
    return 4;
  else
    return 5;
}



export const ConvertNumberToExpressionType = (n: number): ExpressionType => {
  if ((n < 0) || (n > 5))
    return 'undefined';
  else if (n === 0)
    return 'inspiring';
  else if (n === 1)
    return 'appreciative';
  else if (n === 2)
    return 'smart';
  else if (n === 3)
    return 'perceptive';
  else if (n === 4)
    return 'informative';
  else
    return 'funny';
}

export type VotesConfigData = {
  itemId: string,
  impressions: number;
  likes: number;
  comments: number;
  expression?: ExpressionType;
  created: number;
  updated: number;
  popularity?: number;
  affect?: number;
  react?: number;
  userLiked?: boolean;
  usedBoosts?: number;
  floor?: { start: number, end: number };
};

export const EmpyVotesConfig = (): VotesConfigData => {
  return {
    itemId: '',
    impressions: 0,
    likes: 0,
    comments: 0,
    created: 0,
    updated: 0
  }
}

const _e15 = Math.pow(Math.E, 1.5);
const _vpop_alpha = 0.000961856; //1.89485E-05;
const _vpop_delta = 3.13939468;
const _max_popularity_baseline = 1.55;

export const CalculatePopularityForVotes = (votes: VotesConfigData): number => {
  const time = votes.updated - votes.created;
  const baseline = (Math.log(_e15 + _vpop_alpha * time) - 1.5) / _vpop_delta;
  votes.popularity = votes.impressions / baseline;
  return votes.popularity;
}

export const GeneratePopularity = (curated: boolean, multiplier?: number, boost: number = 0, online: number = 0) => {
  const alpha = curated ? 0.25 : 0.6;
  const range = curated ? 0.83 : 0.9;
  const min = curated ? 0.05 : 0.01;
  const max = curated ? 1.0 : 0.9;
  const scale = (curated ? 11973 : 464) / (multiplier ?? 1.0);
  const r = Math.random() * range;
  const raw = 10 / Math.pow(1 - r, 1 / alpha);
  const pop = (raw / scale) * (max - min) + min;
  if (pop > 1.0 || pop < min) {
    console.log('popularity= ' + pop + ' alpha = ' + alpha + ' max = ' + range + ' scale = ' + scale + ' r = ' + r + 'max = ' + raw + ' pop = ' + pop);
  }
  if (online > 1) {
    const pop2 = pop * online + boost;
    return Math.min(pop2, online * 2.0);
  }
  else
    return pop;
}


export const GenerateAffectReactPairPost = (): [number, number] => {
  const r1 = 0.4 + 0.2 * Math.random();
  const r2 = 0.04 + 0.025 * Math.random();
  return [r1, r2];
}

export const GenerateAffectReactPairComment = (): [number, number] => {
  const r1 = 10.4 + 2.6 * Math.random();
  const r2 = (6.16 + 1.54 * Math.random()) * r1;
  return [1 / r1, 1 / r2];
}

export const GetUserCommentTimes = (votes: VotesConfigData, _start: number = -1): number[] => {

  const start = _start < 0 ? Date.now() : _start;
  const maxComments = Math.floor(_max_popularity_baseline * (votes.popularity ?? 1.0) / (11.7 * 6.93));
  const ret: number[] = [];
  const delta = _vpop_delta * 11.7 * 6.93 / (votes.popularity ?? 1.0);

  for (let n = 1; n <= maxComments; n++) {
    const f1 = 1.5 + n * delta;
    const t = (Math.exp(f1) - _e15) / _vpop_alpha;
    ret.push(start + t);
  }

  return ret;
}

export const ProjectVotingForChat = (votes: VotesConfigData, newTime: number, online: number, _floor?: { start: number, end: number }) => {
  votes.impressions = online;
  if (votes.floor === undefined) {
    votes.floor = _floor;
  }

  var floor = votes.floor === undefined ? { start: 0, end: 0 } : votes.floor;
  if (newTime < votes.created) {
    votes.likes = floor.start * 15;
    votes.comments = floor.start;
  }
  else {
    const delta = votes.updated > votes.created ? newTime - votes.updated : newTime - votes.created;
    const floorValue = Math.ceil(15 * (floor.start + (floor.end - floor.start) * (0.65 * (delta * 0.0001) + 0.35)));

    const likesLift = Math.round((0.5 + Math.random()) * (delta * 0.001) * online);
    const commentsLift = Math.round((0.5 + Math.random()) * (delta * 0.00005) * online);
    votes.likes = Math.max(floorValue, votes.likes  + likesLift);
    votes.comments = Math.max(Math.ceil(floorValue * 0.0067), votes.comments + commentsLift);
  }
}

const pva_alpha = 1.16;
const pva_alpha_prime = 0.13793103; // = 1 - 1/alpha

export const ProjectVoting = (votes: VotesConfigData, newTime: number, totalImpressions: number, optionality: number, timeBasis: number = 60000, _boostRate?: number, _floor?: { start: number, end: number }) => {

  if (votes.floor === undefined) {
    votes.floor = _floor;
  }

  var floor = votes.floor === undefined ? { start: 0, end: 0 } : votes.floor;
  if (newTime < votes.created) {
    votes.likes = Math.max(votes.likes, floor.start);
  }
  else {
    const delta = votes.updated > votes.created ? newTime - votes.updated : newTime - votes.created;
    const deltaP = delta / timeBasis;
    const floorValue = Math.ceil(floor.start + (floor.end - floor.start) * (0.65 * deltaP + 0.35));
    if (optionality === 1) {
      const [affect, react] = (votes.affect && votes.react) ? [votes.affect, votes.react] : (() => { const [a, r] = GenerateAffectReactPairPost(); votes.affect = a; votes.react = r; return [a, r] })(); // if affect and react are not initialized, initialize them

      if (votes.popularity === undefined)
        votes.popularity = 10000;

      votes.impressions = Math.round(totalImpressions * votes.popularity * 0.0001);
      const mmax = totalImpressions * react;
      const time = Math.min(votes.comments / mmax + deltaP, 1);
      votes.comments = Math.round(time * mmax);
      votes.likes = Math.max(Math.round(Math.pow(Math.sin(time * 0.5 * Math.PI), 0.25) * totalImpressions * affect), floorValue, votes.likes);
      votes.updated = newTime;
    }
    else {
      const boostRate = _boostRate ?? 0;
      const usedBoosts = votes.usedBoosts ?? 0;

      const organic = votes.impressions - usedBoosts;
      const newBoosts = Math.floor(delta / 3600000 * boostRate);
      votes.usedBoosts = usedBoosts + newBoosts;
      const distMin = 1 / optionality;
      //Takes the integral of the pareto function form half the lowest value (imaging a 1 unit bar centered on the lowest value) up to 1 + half the lowest value (same again at top of the range). Remember 0 tends to infinity for pareto functions.
      const alow = 1 / pva_alpha_prime * Math.pow(distMin * 0.5, pva_alpha_prime);
      const ahigh = 1 / pva_alpha_prime * Math.pow(1 + distMin * 0.5, pva_alpha_prime);
      const area = ahigh - alow; // The total area of the pareto function of that optionality.


      if (votes.popularity === undefined)
        votes.popularity = Math.floor(Math.random() * optionality); // Just in case popularty is undefined it gets randomly assigned.

      const fmax = (1 / Math.pow(votes.popularity / optionality, pva_alpha)); // The raw output of the pareto function
      const nmax = fmax / optionality / area; // remembering the width of the bar is 1/optionality and to normalize the pareto function we need to divide by area

      const maxImpressions = totalImpressions * nmax;

      var projection = 0;

      if ((votes.popularity / optionality) <= 0.2) {
        const oprop = organic / maxImpressions;
        if (oprop >= 1.0) // Safety scenario where the impressions are already on or past the theoretical limit.
          projection = maxImpressions;
        else {
          const gamma = Math.acos(-2 * (oprop - 0.5)); // Use an upside-down cos function as an approximation to a a sigma function. Acos returns the PI * proportion of time (0->1)
          const dprop = gamma + deltaP * Math.PI;
          if (dprop > Math.PI) // Make sure your not taking the number of impressions past them limit which actually causes a decrease
            projection = maxImpressions;
          else
            projection = (0.5 + (Math.cos(dprop) * -0.5)) * maxImpressions; //convert delta into a proportion of time * PI, then can add gamma and take the Cos (turning upside down and mapping it 0->1) to get the projected value.
        }
      }
      else {
        projection = Math.min(maxImpressions, organic + deltaP * maxImpressions); // simple linear projection
      }

      if (isNaN(projection)) {
        console.log("Error in voting projection, is Nan");
      }

      const [affect, react] = (votes.affect && votes.react) ? [votes.affect, votes.react] : (() => { const [a, r] = GenerateAffectReactPairComment(); votes.affect = a; votes.react = r; return [a, r] })(); // if affect and react are not initialized, initialize them
      votes.impressions = Math.round(projection + votes.usedBoosts);
      votes.likes = Math.max(Math.round((projection + votes.usedBoosts) * affect), floorValue, votes.likes);
      votes.comments = Math.round((projection + votes.usedBoosts) * react);
      votes.updated = newTime;
    }
  }
  if (votes.likes < floor.start) {
    console.log('Voting likes failed to updated properly - ' + votes.likes);
  }

}

/*export const ProjectVoting = (votes: VotesConfigData, newTime: number) => {
  const time = newTime - votes.created;

  const popularity = votes.popularity ?? CalculatePopularityForVotes(votes);
  const projection = popularity * Math.min(_max_popularity_baseline, (Math.log(_e15 + _vpop_alpha * time) - 1.5) / _vpop_delta);
  const [affect, react] = (votes.affect && votes.react) ? [votes.affect, votes.react] : (() => { const [a, r] = GenerateAffectReactPairComment(); votes.affect = a; votes.react = r; return [a, r] })();
  votes.impressions = Math.round(projection);
  votes.likes = Math.round(projection * affect);
  votes.comments = Math.round(projection * react);
  votes.updated = newTime;
}*/

export const InitialiseImpressions = (votes: VotesConfigData, delta: number, online: number): [number, number] => {
  const popularity = votes.popularity ?? 0.1 * online * 2.0;
  const projection = popularity * (Math.log(_e15 + _vpop_alpha * delta) - 1.5) / _vpop_delta;
  return [popularity, projection];
}

export type QuizBankUsage = "sequential" | "interleaved-ordered" | "interleaved-random";
export type QuizType = 'quiz' | 'survey' | 'profile';

export type QuizTreeAlgorithm = "map" | "AgtB" | "threshA" | "threshAorB" | "threshAandB" | "threshAnotB" | "threshAxorB" | "threshAxB";
export type QuizTimerMode = "none" | "question" | "total";
export type QuizImageAspectRatio = "21:9" | "16:9" | "3:2" | "4:3" | "1:1" | "3:4" | "2:3" | "9:16" | "9:21";
export type QuizAnswerType = "select" | "matchAnswer" | "slide" | "counterSlide";

export type QuizConfigData = {
  itemId: string;
  quizType: QuizType;
  title: string;
  subTitle: string;
  description: string;
  image: string;
  timerMode: QuizTimerMode;
  timeBonusMultiplier?: number;
  questionTimer?: number;
  scrambleQuestions: boolean;
  scrambleAnswers: boolean;
  pointsPerQuestion: number;
  bankUsage: QuizBankUsage;
  banks: QuizBankConfig[];
  metrics: string[];
  scores?: QuizScores;
  survey?: QuizSurveyResults;
  defaultProfile?: QuizProfile;
}

export const ConvertQuizToFlatString = (quiz: QuizConfigData): string[] => {
  var output = Array<string>();
  quiz.banks.forEach((bank) => {
    bank.simpleQuestions.forEach((question) => {
      var qstr = '<Question><QuestionText>"' + (question.text ?? '') + '</QuestionText><Answers>';
      question.answers.forEach((answer, index) => {

        qstr += "<Answer><AnswerText>" + (answer.text ?? '') + "</AnswerText><IsCorrect>" + (answer.correct ? 'Correct' : 'Incorrect') + "</IsCorrect></Answer>";
      });
      qstr += "</Answers></Question>";
      output.push(qstr);
    });

  });
  return output;
}

export const CheckQuizConfigData = (data: QuizConfigData, errors: string[]) => {
  if (data.itemId === undefined)
    errors.push('QuizConfigData.itemId is undefined');
  if (data.quizType === undefined)
    errors.push('QuizConfigData.quizType is undefined');
  else if (data.quizType !== 'quiz' &&
    data.quizType !== 'survey' &&
    data.quizType !== 'profile')
    errors.push('QuizConfigData.quizType bad value = ' + data.quizType);

  if (data.title === undefined)
    errors.push('QuizConfigData.title is undefined');
  if (data.subTitle === undefined)
    errors.push('QuizConfigData.subTitle is undefined');
  if (data.description === undefined)
    errors.push('QuizConfigData.description is undefined');
  if (data.timerMode === undefined)
    errors.push('QuizConfigData.timerMode is undefined');
  else if (data.timerMode !== "none" &&
    data.timerMode !== "question" && data.timerMode !== "total")
    errors.push('QuizConfigData.timeMode bad value = ' + data.timerMode);

  if (data.scrambleQuestions === undefined)
    errors.push('QuizConfigData.scrambleQuestions is undefined');
  if (data.scrambleAnswers === undefined)
    errors.push('QuizConfigData.scrambleAnswers is undefined');
  if (data.pointsPerQuestion === undefined)
    errors.push('QuizConfigData.pointsPerQuestion is undefined');
  if (data.bankUsage === undefined)
    errors.push('QuizConfigData.bankUsage is undefined');
  else if (data.bankUsage !== "sequential" && data.bankUsage !== "interleaved-ordered" && data.bankUsage !== "interleaved-random")
    errors.push('QuizConfigData.bankUsage bad value = ' + data.bankUsage);

  if (data.banks === undefined)
    errors.push('QuizConfigData.banks is undefined');
  else {
    for (let i = 0; i < data.banks.length; i++) {
      errors.push('Checking QuizConfigData.banks[' + i + ']');
      CheckQuizBankConfig(data.banks[i], errors);
    }
  }

  if (data.metrics === undefined)
    errors.push('QuizConfigData.metrics is undefined');

  if (data.scores !== undefined) {
    errors.push('Checking QuizConfigData.scores');
    CheckQuizScores(data.scores, errors);
  }

  if (data.survey !== undefined) {
    errors.push('Checking QuizConfigData.survey');
    CheckQuizSurveyResults(data.survey, errors);
  }

  if (data.defaultProfile !== undefined) {
    errors.push('Checking QuizConfigData.defaultProfile');
    CheckQuizProfile(data.defaultProfile, errors);
  }

}

export const CloneForQuizClient = (quiz: QuizConfigData): QuizConfigData => {
  return {
    itemId: quiz.itemId,
    quizType: quiz.quizType,
    title: quiz.title,
    subTitle: quiz.subTitle,
    description: quiz.description,
    image: quiz.image,
    timerMode: quiz.timerMode,
    timeBonusMultiplier: quiz.timeBonusMultiplier,
    questionTimer: quiz.questionTimer,
    scrambleQuestions: quiz.scrambleQuestions,
    scrambleAnswers: quiz.scrambleAnswers,
    pointsPerQuestion: quiz.pointsPerQuestion,
    bankUsage: quiz.bankUsage,
    banks: quiz.banks,
    metrics: quiz.metrics
  }
}

export type QuizProfile = {
  title: string;
  categorization: string;
  catText: string;
  catImage?: string;
  components: { heading: string, text: string, image?: string }[];
}

const CheckQuizProfile = (data: QuizProfile, errors: string[]) => {
  if (data.title === undefined)
    errors.push('QuizScores.title is undefined');
  if (data.categorization === undefined)
    errors.push('QuizScores.categorization is undefined');
  if (data.catText === undefined)
    errors.push('QuizScores.catText is undefined');
  if (data.components === undefined)
    errors.push('QuizScores.componentns is undefined');
  else {
    for (let i = 0; i < data.components.length; i++) {
      errors.push('Checking QuizProfile.components[' + i + ']');
      if (data.components[i].text === undefined)
        errors.push('QuizScores.components.text is undefined');
      if (data.components[i].heading === undefined)
        errors.push('QuizScores.components.heading is undefined');
    }
  }
}

export type QuizScoreRanking = { nickname: string, score: number };

export type QuizScores = {
  rankings: QuizScoreRanking[];
  averageCorrect: number;
  averageQuestionScore: number;
  averageTime: number;
  averageTimeBonus: number;
  uniquePlayers: number;
  shareAction: UserAction;
}

const CheckQuizScores = (data: QuizScores, errors: string[]) => {
  if (data.rankings === undefined)
    errors.push('QuizScores.rankings is undefined');
  else {
    for (let i = 0; i < data.rankings.length; i++) {
      errors.push('Checking QuizScores.rankings[' + i + ']');
      if (data.rankings[i].nickname === undefined)
        errors.push('QuizScoreRanking.nickname is undefined');
      if (data.rankings[i].score === undefined)
        errors.push('QuizScoreRanking.score is undefined');
    }
  }
  if (data.averageCorrect === undefined)
    errors.push('QuizScores.averageCorrect is undefined');
  if (data.averageQuestionScore === undefined)
    errors.push('QuizScores.averageQuestionScore is undefined');
  if (data.averageTime === undefined)
    errors.push('QuizScores.averageTime is undefined');
  if (data.averageTimeBonus === undefined)
    errors.push('QuizScores.averageTimeBonus is undefined');
  if (data.uniquePlayers === undefined)
    errors.push('QuizScores.uniquePlayers is undefined');
  if (data.shareAction !== undefined) {
    errors.push('Checking QuizScores.shareAction');
    CheckUserAction(data.shareAction, errors);
  }


}

export type QuizSurveyAnswerResult = {
  name: string;
  votes: number;
}

export type QuizSurveyQuestionResult = {
  results: QuizSurveyAnswerResult[],
  question: string;
  annotation: string;

}

const CheckQuizSurveyQuestionResult = (data: QuizSurveyQuestionResult, errors: string[]) => {
  if (data.question === undefined)
    errors.push('QuizSurveyQuestionResult.question is undefined');

  if (data.annotation === undefined)
    errors.push('QuizSurveyQuestionResult.annotation is undefined');

  if (data.results === undefined)
    errors.push('QuizSurveyQuestionResult.results is undefined');
  else {
    for (let i = 0; i < data.results.length; i++) {
      errors.push('Checking QuizSurveyQuestionResult.results[' + i + ']');
      if (data.results[i].name === undefined)
        errors.push('QuizSurveyAnswerResult.name is undefined');
      if (data.results[i].votes === undefined)
        errors.push('QuizSurveyAnswerResult.votes is undefined');
    }
  }

}

export type QuizSurveyResults = {
  questions: QuizSurveyQuestionResult[]
}

const CheckQuizSurveyResults = (data: QuizSurveyResults, errors: string[]) => {
  if (data.questions === undefined)
    errors.push('QuizSurveyResults.questions is undefined');
  else {
    for (let i = 0; i < data.questions.length; i++) {
      errors.push('Checking QuizSurveyResults.questions[' + i + ']');
      CheckQuizSurveyQuestionResult(data.questions[i], errors);
    }
  }
}

export type QuizResults = {
  results: { [key: string]: QuizResult[] };
  timeBonus: number;
  answerPoints: number;
}

/*const CheckQuizResults = (data : QuizResults, errors : string[]) => {
  if(data.timeBonus === undefined)
    errors.push('QuizResults.timeBonus is undefined');

  if(data.answerPoints === undefined)
    errors.push('QuizResults.answerPoints is undefined');

  if(data.results === undefined)
    errors.push('QuizResults.results is undefined');
  else{
    Object.keys(data.results).forEach((key)=> {
      const qsr = data.results[key];
      errors.push('Checking QuizResults.results[' + key + ']');
      if(qsr.)
    })
  }

}*/

export const EmptyQuizResults = (): QuizResults => {
  return {
    results: {},
    timeBonus: 0,
    answerPoints: 0
  };
}

export type QuizResult = {
  questionId: string;
  answers: QuizAnswerConfig[];
  selected: number[];
  correct: boolean;
  points: number;
  values: number[];
  metrics: { name: string, value: number }[];
  timeTaken: number;
}

export type QuizPosition = {
  absolutePosition: number;
  relativePosition: number;
  absoluteScore: number;
  relativeScore: number;
}

const UtoNContextQuizScores: UniformToNormalDistributionContext = {
  uniformLow: 0.0,
  uniformHigh: 1.0,
  normalLow: 0.0,
  normalHigh: 1.0
}


export const TestGeneratorRandomQuizScore = (field?: QuizScores): { data: Array<{ start: QuizPosition, end: QuizPosition }>, fails: number } => {

  const data = Array<{ start: QuizPosition, end: QuizPosition }>();
  var fails = 0;
  const topScore: QuizScoreRanking = {
    nickname: 'Top Scorer',
    score: 4200
  };

  const shareAction: ShareActionConfig = {
    titles: Array<string>(),
    texts: Array<string>(),
    images: Array<string>(),
    buttonTexts: Array<string>(),
    buttonAction: 'Share'
  }

  const userAction: UserAction = {
    itemId: '',
    actionType: 'share',
    config: shareAction,
    title: '',
    bodyText: '',
    image: '',
    multiplier: 1.0,
    buttonText: 'share',
    created: Date.now(),
    status: 'moderating',
    statusText: 'In Moderation'
  }

  const qscores: QuizScores = field !== undefined ? field : {
    rankings: [topScore],
    averageCorrect: 15,
    averageQuestionScore: 1500,
    averageTime: 90000,
    averageTimeBonus: 600,
    uniquePlayers: 10000,
    shareAction: userAction
  }

  const numberGenerator = SequentialNumberGenerator(0.01, 0.01);

  for (let i = 0; i < 100; i++) {
    const start = GenerateRandomQuizScore(numberGenerator, qscores);

    const qResults: QuizResults = {
      results: {},
      timeBonus: 0,
      answerPoints: start.absoluteScore
    }

    const end = CalculateQuizScorePosition(qResults, qscores);

    const error = Math.abs(start.relativePosition - end.relativePosition) + Math.abs(start.relativeScore - end.relativeScore);
    if (error > 0.01)
      fails++;
    data.push({ start, end });
  }

  return { data, fails };
}

export const GenerateRandomQuizScore = (generator: NumberGenerator, field: QuizScores): QuizPosition => {

  const relativePosition = generator();
  const absolutePosition = Math.floor(relativePosition * field.uniquePlayers) + 1;
  const relativeScore = UniformToNormalTransform((1 - relativePosition), UtoNContextQuizScores);

  const avgScore = field.averageQuestionScore + field.averageTimeBonus;

  var absoluteScore = 0;
  if (relativeScore < 0.5)
    absoluteScore = Math.round((relativeScore * 2.0) * avgScore);
  else
    absoluteScore = Math.round(avgScore + (relativeScore - 0.5) * 2.0 * (field.rankings[0].score - avgScore));

  const ret: QuizPosition = { absolutePosition, relativePosition, relativeScore, absoluteScore };
  return ret;
}

export const CalculateQuizScorePosition = (results: QuizResults, field: QuizScores): QuizPosition => {
  const absoluteScore = results.answerPoints + results.timeBonus;

  const avgScore = field.averageQuestionScore + field.averageTimeBonus;
  //const avgPos = avgScore / field.rankings[0].score;

  var relativeScore = 0;

  if (absoluteScore < avgScore) {
    relativeScore = 0.5 * absoluteScore / avgScore;
  }
  else {
    relativeScore = 0.5 * (1 + (absoluteScore - avgScore) / (field.rankings[0].score - avgScore));
  }

  //var range = 0.5 * field.rankings[0].score; //2 * (field.rankings[0].score - field.averageQuestionScore - field.averageTimeBonus);
  //range = 0.5 * Math.min(field.rankings[0].score, range);

  //var relativeScore = (score - field.averageQuestionScore - field.averageTimeBonus) / range;
  //relativeScore = relativeScore > 1.0 ? 1.0 : (relativeScore < -1.0  ? -1.0 : relativeScore);

  const relativePosition = NormalToUniformTransform(1 - relativeScore, UtoNContextQuizScores);

  //const relativePosition = Math.asin(relativeScore) / Math.PI;
  var absolutePosition = Math.floor(relativePosition * field.uniquePlayers) + 1;
  return { absolutePosition, relativePosition, relativeScore, absoluteScore };
}


export type QuizBankConfig = {
  itemId: string;
  questionTimer?: number;
  scrambleQuestions?: boolean;
  scrambleAnswers?: boolean;
  pointsPerQuestion?: number;
  pickQuestions?: number;
  simpleQuestions: QuizQuestionConfig[];
  adaptiveQuestions: QuizTreeConfig[];

};

const CheckQuizBankConfig = (data: QuizBankConfig, errors: string[]) => {
  if (data.itemId === undefined)
    errors.push('QuizBankConfig.itemId is undefined');
  if (data.simpleQuestions === undefined)
    errors.push('QuizBankConfig.simpleQuestions is undefined');
  else {
    for (let i = 0; i < data.simpleQuestions.length; i++) {
      errors.push('Checking QuizBankConfig.simpleQuestions[' + i + ']');
      CheckQuizQuestionConfig(data.simpleQuestions[i], errors);
    }
  }

  if (data.adaptiveQuestions === undefined)
    errors.push('QuizBankConfig.adaptiveQuestions is undefined');
  else {
    for (let i = 0; i < data.adaptiveQuestions.length; i++) {
      errors.push('Checking QuizBankConfig.adaptiveQuestions[' + i + ']');
      CheckQuizTreeConfig(data.adaptiveQuestions[i], errors);
    }
  }
}

export type QuizTreeConfig = {
  itemId: string;
  metricA?: string;
  metricB?: string;
  threshA?: number;
  threshB?: number;
  algo: QuizTreeAlgorithm;
  question: QuizQuestionConfig;
  children?: QuizTreeConfig[];
};

const CheckQuizTreeConfig = (data: QuizTreeConfig, errors: string[]) => {
  if (data.itemId === undefined)
    errors.push('QuizTreeConfig.itemId is undefined');

  if (data.algo === undefined)
    errors.push('QuizTreeConfig.algo is undefined');
  else if (data.algo !== "map" &&
    data.algo !== "AgtB" &&
    data.algo !== "threshA" &&
    data.algo !== "threshAorB" &&
    data.algo !== "threshAandB" &&
    data.algo !== "threshAnotB" &&
    data.algo !== "threshAxorB" &&
    data.algo !== "threshAxB")
    errors.push('QuizTreeConfig.algo bad value = ' + data.algo);
  if (data.question === undefined)
    errors.push('QuizTreeConfig.question is undefined');
  else {
    errors.push('Checking QuizTreeConfig.question');
    CheckQuizQuestionConfig(data.question, errors);
  }

  if (data.children !== undefined) {
    for (let i = 0; i < data.children.length; i++) {
      errors.push('Checking QuizTreeConfig.children[' + i + ']');
      CheckQuizTreeConfig(data.children[i], errors);
    }
  }

}

export type QuizQuestionConfig = {
  itemId: string;
  text?: string;
  images?: string[];
  answers: QuizAnswerConfig[];
  scramble?: boolean;
  addAnswersToImages?: string[];
  addAnswers?: string[];
  pickAnswers?: number;
  points?: number;
  questionTimer?: number;
  metricName?: string;
  metricPoints?: number;
  generator?: string[];
  generateRounds?: number;
  questionImageAspect?: QuizImageAspectRatio;
  answerImageAspect?: QuizImageAspectRatio;
  answerType?: QuizAnswerType;
  refill?: boolean;
  refillCount?: number;

}

const checkQuizAspectRatio = (value: string, name: string, errors: string[]) => {
  if (value !== "21:9" && value !== "16:9" && value !== "3:2" && value !== "4:3" && value !== "1:1" && value !== "3:4" &&
    value !== "2:3" && value !== "9:16" && value !== "9:21")
    errors.push(name + ' has bad value = ' + value);
}

const CheckQuizQuestionConfig = (data: QuizQuestionConfig, errors: string[]) => {
  if (data.itemId === undefined)
    errors.push('QuizQuestionConfig.itemId is undefined');

  if (data.text === undefined && data.images === undefined)
    errors.push('QuizQuestionConfig.text and QuizQuestionConfig.images are both undefined');

  if (data.answers === undefined)
    errors.push('QuizQuestionConfig.answers is undefined');
  else {
    for (let i = 0; i < data.answers.length; i++) {
      errors.push('Checking QuizQuestionConfig.answers[' + i + ']');
      CheckQuizAnswerConfig(data.answers[i], errors);
    }
  }

  if (data.questionImageAspect !== undefined)
    checkQuizAspectRatio(data.questionImageAspect, 'QuizQuestionConfig.questionImageAspect', errors);
  if (data.answerImageAspect !== undefined)
    checkQuizAspectRatio(data.answerImageAspect, 'QuizQuestionConfig.answerImageAspect', errors);


}

export type QuizAnswerConfig = {
  correct?: boolean;
  text?: string;
  image?: string;
  matchId?: string;
  itemId: string;
}

const CheckQuizAnswerConfig = (data: QuizAnswerConfig, errors: string[]) => {
  if (data.itemId === undefined)
    errors.push('QuizAnswerConfig.itemId is undefined');

  if (data.text === undefined && data.image === undefined)
    errors.push('QuizAnswerConfig.text and QuizAnswerConfig.image are both undefined');

}

export type CreativeFeatureOption = {
  text: string; // What the user sees
  image?: string[]; // Array of reference image URLs/paths, this is optional
}

export type CreativeFeature = {
  text: string; // Question displayed to user
  options: CreativeFeatureOption[]; // Available choices
  selectCount: number// Whether multiple options can be selected
}

export type CreativeTopic = {
  text: string;
  attribution: string;
  features: CreativeFeature[];
}

export type CreativeConfigData = {
  itemId: string;
  topics: CreativeTopic[];
  image: string;
  title: string;
  subTitle: string;
  description: string;
  buttonText: string;
  thankYouText: string;

}

export type SubmissionCompetitionConfigData = {
  itemId: string;
  submission: DynamicFormElement[];
  title: string;
  subTitle: string;
  description: string;
  thankYouMessage: string;
  image: string;
  buttonText: string;
  participantLimit: number;
  perParticipant: number;
}

export const CheckSubmissionCompetitionConfigData = (data: SubmissionCompetitionConfigData, errors: string[]) => {
  if (data.itemId === undefined)
    errors.push('SubmissionCompetitionConfigData.itemId is undefined');

  if (data.submission === undefined)
    errors.push('SubmissionCompetitionConfigData.submission is undefined');
  else {
    for (let i = 0; i < data.submission.length; i++) {
      errors.push('Checking SubmissionCompetitionConfigData.submission[' + i + ']');
      CheckDynamicFormElement(data.submission[i], errors);
    }
  }
  if (data.title === undefined)
    errors.push('SubmissionCompetitionConfigData.title is undefined');
  if (data.subTitle === undefined)
    errors.push('SubmissionCompetitionConfigData.subTitle is undefined');
  if (data.description === undefined)
    errors.push('SubmissionCompetitionConfigData.description is undefined');
  if (data.thankYouMessage === undefined)
    errors.push('SubmissionCompetitionConfigData.thankYouMessage is undefined');
  if (data.image === undefined)
    errors.push('SubmissionCompetitionConfigData.image is undefined');
  if (data.buttonText === undefined)
    errors.push('SubmissionCompetitionConfigData.buttonText is undefined');
  if (data.participantLimit === undefined)
    errors.push('SubmissionCompetitionConfigData.participantLimit is undefined');
  if (data.perParticipant === undefined)
    errors.push('SubmissionCompetitionConfigData.perParticipant is undefined');

}





export const getAllMediaURLs = (party: PartyConfigData): Array<string> => {
  const ret = new Array<string>();
  for (let j = 0; j < party.media.length; j++) {
    ret.push(party.media[j].url);
  }
  return ret;
}


